import { useState, useEffect, useCallback, useId } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './useAuth';

export const useContacts = () => {
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  // Nome único por instância: useContacts é montado em Dashboard, EmailCoveragePage,
  // HistoryModal simultaneamente — sem isso, o 2º mount reaproveita o channel já
  // subscrito e o .on() falha com "cannot add ... after subscribe()".
  const channelId = useId();

  // --- Busca Principal (EMPRESA-CÊNTRICA) ---
  const carregarContatos = useCallback(async () => {
    try {
      // 1. Busca contatos com empresas (como antes)
      const { data: contatosData, error: contatosError } = await supabase
        .from('contatos')
        .select(`*, empresas!contatos_empresa_id_fkey(*)`) // cpf já vem via *
        .order('created_at', { ascending: false });
      
      if (contatosError) throw contatosError;

      // 2. Busca TODAS as empresas
      const { data: empresasData, error: empresasError } = await supabase
        .from('empresas')
        .select('*');
      
      if (empresasError) throw empresasError;

      // 3. Identifica empresas que NÃO têm nenhum contato
      const empresasComContatos = new Set(
        (contatosData || []).map(c => c.empresa_id)
      );
      const empresasSemContatos = (empresasData || []).filter(
        e => !empresasComContatos.has(e.id)
      );

      // 4. Cria entradas virtuais para empresas sem contatos
      //    Isso permite que elas apareçam na tabela agrupada
      const virtuais = empresasSemContatos.map(e => ({
        id: `virtual-${e.id}`,
        empresa_id: e.id,
        first_name: null,
        last_name: null,
        title: null,
        contact_stage: null,
        email: null,
        email_status: null,
        phone: null,
        mobile: null,
        responsavel: null,
        observacao: null,
        person_linkedin_url: null,
        colaborador_nome: null,
        colaborador_email: null,
        lists: null,
        apollo_contact_id: null,
        secondary_email: null,
        created_at: e.created_at,
        empresas: e,
        _isVirtual: true
      }));

      setContatos([...(contatosData || []), ...virtuais]);
    } catch (err) {
      console.error('Erro ao carregar contatos:', err);
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      await carregarContatos();
      setLoading(false);
    };
    fetchInitialData();

    const channel = supabase.channel(`contatos-realtime-${channelId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contatos' }, () => carregarContatos())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'empresas' }, () => carregarContatos())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [carregarContatos, channelId]);

  // --- Updates Basicos ---
  // Onda 2 (abr/26): toda atualização inline passa a carimbar updated_at/updated_by
  // automaticamente. Chamadores não precisam mais injetar esses campos manualmente
  // (continuam funcionando — o spread preserva overrides explícitos).
  const stampAudit = (updates) => ({
    updated_at: updates.updated_at ?? new Date().toISOString(),
    updated_by: updates.updated_by ?? (user?.email || user?.id || null),
    ...updates,
  });

  const updateContact = async (id, updates) => {
    try {
      const { error } = await supabase.from('contatos').update(stampAudit(updates)).eq('id', id);
      if (error) throw error;
      await carregarContatos();
      return { success: true };
    } catch (err) {
       console.error('Erro ao atualizar contato:', err);
       return { success: false, error: err.message };
     }
  };

  const updateEmpresa = async (id, updates) => {
    try {
      const { error } = await supabase.from('empresas').update(stampAudit(updates)).eq('id', id);
      if (error) throw error;
      await carregarContatos();
      return { success: true };
    } catch (err) {
        console.error('Erro ao atualizar empresa:', err);
        return { success: false, error: err.message };
     }
  };

  const createContact = async (newContact) => {
    try {
        const { error } = await supabase.from('contatos').insert([newContact]);
        if (error) throw error;
        await carregarContatos();
        return { success: true };
    } catch (err) {
        console.error('Erro ao criar contato:', err);
        return { success: false, error: err.message };
    }
  };

  // --- CRIAR EMPRESA (NOVO) ---
  const createEmpresa = async (newEmpresa) => {
    try {
        const { data, error } = await supabase
          .from('empresas')
          .insert([newEmpresa])
          .select();
        if (error) throw error;
        await carregarContatos();
        return { success: true, data: data?.[0] };
    } catch (err) {
        console.error('Erro ao criar empresa:', err);
        return { success: false, error: err.message };
    }
  };

  // --- LOGICA DO HISTORICO ---

  const fetchHistory = async (empresaId) => {
    try {
      const { data, error } = await supabase
        .from('historico_interacoes')
        .select('*, contatos(first_name, last_name)') 
        .eq('empresa_id', empresaId)
        .order('data_evento', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Erro ao buscar historico:", err);
      return [];
    }
  };

  const addHistory = async (empresaId, dados) => {
    try {
        const { error: histError } = await supabase
            .from('historico_interacoes')
            .insert([{
                empresa_id: empresaId,
                texto: dados.texto,
                contato_id: dados.contato_id || null,
                data_evento: dados.data_evento || new Date().toISOString().split('T')[0],
                user_id: user?.id,
                user_email: user?.email,
                tipo: 'manual'
            }]);
        
        if (histError) throw histError;

        return { success: true };
    } catch (err) {
        console.error("Erro ao adicionar historico:", err);
        return { success: false, error: err.message };
    }
  };

  const updateHistory = async (historyId, updates) => {
      try {
          const { error } = await supabase
            .from('historico_interacoes')
            .update(updates)
            .eq('id', historyId);
          if (error) throw error;
          
          return { success: true };
      } catch (err) {
          console.error("Erro ao atualizar historico:", err);
          return { success: false, error: err.message };
      }
  };

  const deleteHistory = async (historyId) => {
      try {
          const { error } = await supabase
            .from('historico_interacoes')
            .delete()
            .eq('id', historyId);
          if (error) throw error;
          return { success: true };
      } catch (err) {
          console.error("Erro ao deletar historico:", err);
          return { success: false, error: err.message };
      }
  };

  const manualRecarregar = async () => {
      setLoading(true);
      await carregarContatos();
      setLoading(false);
  };

  return { 
    contatos, 
    loading, 
    error, 
    updateContact, 
    updateEmpresa, 
    createContact,
    createEmpresa,
    fetchHistory, 
    addHistory, 
    updateHistory, 
    deleteHistory,
    recarregar: manualRecarregar 
  };
};
